/**
 * R-14 T-088 — while an Administrator views as a persona, every page says so and offers the way back.
 * John, 2026-10-21: off production a write made while viewing as someone is allowed and recorded as the
 * real person, with the persona on the record of action; the banner says whose name it is saved under.
 */
import { Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useMe } from '@/api/queries';
import { useViewAs, useViewAsControl } from '@/app/experience/experience-api';
import { Button, requestDrawerLeave } from '@/kit';

/** "<role label> · <department>": the role in business words, never its code. */
export function personaName(departmentName: string | null | undefined, roleLabel: string | null | undefined): string {
  const r = roleLabel || 'another role';
  return departmentName ? `${r} · ${departmentName}` : r;
}

/** The banner's sentence: who the screen is shown as, and whose name anything recorded is saved under. */
export function viewAsSentence(who: string, realName: string | null | undefined): string {
  const you = realName?.trim() ? `you, ${realName.trim()},` : 'you,';
  return `Viewing as ${who}. Anything you record here is saved as ${you} viewing as this person.`;
}

export function ViewAsBanner() {
  const me = useMe(true);
  const state = useViewAs(Boolean(me.data));
  const s = state.data;
  if (!s?.active || !s.role) return null;
  const who = personaName(s.department?.name, s.roleLabel ?? s.label ?? s.personas?.roles.find((r) => r.code === s.role)?.label);
  return <Banner who={who} realName={s.realUser?.displayName} />;
}

function Banner({ who, realName }: { who: string; realName: string | null | undefined }) {
  const { stop } = useViewAsControl();
  const navigate = useNavigate();
  // exit returns the admin's own view: their own home, not the persona's page
  // (mutateAsync: the banner unmounts when the reset clears the preview, so a mutate() callback would never run)
  // stopping resets every cached read, so an open record with unsaved edits is asked about first
  const exit = () => void requestDrawerLeave().then(async (ok) => {
      if (ok) await stop.mutateAsync().then(() => navigate('/', { replace: true }));
    });
  return (
    <div role="status" className="k-gutter flex items-center gap-2 bg-warn-soft text-ink" data-testid="view-as-banner">
      <Eye size={14} aria-hidden />
      <p className="k-meta m-0 text-ink">
        {viewAsSentence(who, realName)}
      </p>
      <Button variant="secondary" onClick={exit} disabled={stop.isPending}>
        Stop viewing as {who}
      </Button>
    </div>
  );
}
